"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.prettyPrint = prettyPrint;
exports.formatXml = formatXml;
/**
 * Pretty prints JSON (2 spaces) or XML (DOM-walk indentation).
 * Falls back to returning the original input if parsing fails.
 */
function prettyPrint(input) {
    var trimmed = input.trim();
    if (!trimmed) {
        return input;
    }
    // XML
    if (trimmed.startsWith("<")) {
        return formatXml(trimmed);
    }
    // JSON
    try {
        var json = JSON.parse(trimmed);
        return JSON.stringify(json, null, 2);
    }
    catch (_a) {
        // Not JSON, continue
    }
    return input;
}
/**
 * Pretty-prints XML by parsing it into a DOM and then serializing it back out
 * with deterministic indentation.
 *
 * High-level approach:
 *  1) Parse the input with `DOMParser` to get a real XML DOM.
 *  2) Walk the DOM depth-first and emit one output line at a time.
 *  3) Indentation is derived solely from the current element depth.
 *
 * Formatting rules used during the DOM walk:
 *  - Whitespace-only text nodes that exist purely for formatting in the input
 *    are ignored (so input indentation doesn’t “double indent” the output).
 *  - Empty elements are emitted as self-closing tags: `<tag />`.
 *  - Elements containing exactly one text child are kept on a single line:
 *    `<tag>text</tag>` (useful for simple leaf nodes).
 *  - Elements with nested elements / multiple children are expanded into
 *    a multi-line block with an opening tag, then children, then closing tag.
 *  - Text nodes are normalized (collapsed runs of whitespace to a single space)
 *    so that long/irregular spacing doesn’t produce noisy output.
 *
 * Note: this is intended for “human-readable” output, not for byte-perfect
 * round-tripping. If parsing fails, the original input is returned unchanged.
 */
function formatXml(xml, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var input = xml.trim();
    if (!input) {
        return xml;
    }
    var indentUnit = (_a = options.indentUnit) !== null && _a !== void 0 ? _a : "  ";
    try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(input, "application/xml");
        // DOMParser reports invalid XML by inserting a <parsererror/> element.
        // If we detect that, we bail out and return the original string.
        if (doc.getElementsByTagName("parsererror").length > 0) {
            return xml;
        }
        // Attribute values are serialized manually; escape only what is required
        // for valid XML attribute values.
        var escapeAttr_1 = function (value) {
            return value
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/"/g, "&quot;");
        };
        // Build `<tag attr="value">` (no closing bracket logic here).
        // We intentionally preserve attribute order as provided by the DOM.
        var serializeElementStartTag_1 = function (el) {
            var attrs = el.attributes.length === 0
                ? ""
                : " " +
                    Array.from(el.attributes)
                        .map(function (a) { return "".concat(a.name, "=\"").concat(escapeAttr_1(a.value), "\""); })
                        .join(" ");
            return "<".concat(el.tagName).concat(attrs, ">");
        };
        // Treat whitespace-only text nodes as formatting noise.
        // These commonly come from input like:
        //   <a>
        //     <b/>
        //   </a>
        var isIgnorableWhitespaceText_1 = function (node) {
            var _a;
            return node.nodeType === Node.TEXT_NODE &&
                ((_a = node.nodeValue) !== null && _a !== void 0 ? _a : "").trim().length === 0;
        };
        // Depth-first formatter that appends lines into `out`.
        // `indentLevel` is the current element depth (root elements start at 0).
        var formatNode_1 = function (node, indentLevel, out) {
            var _a, _b, _c, _d;
            var pad = indentUnit.repeat(indentLevel);
            switch (node.nodeType) {
                case Node.DOCUMENT_NODE: {
                    // Walk top-level children but drop ignorable whitespace nodes.
                    node.childNodes.forEach(function (child) {
                        if (!isIgnorableWhitespaceText_1(child)) {
                            formatNode_1(child, indentLevel, out);
                        }
                    });
                    return;
                }
                case Node.ELEMENT_NODE: {
                    var el = node;
                    // We filter out whitespace-only text nodes so that existing
                    // indentation/newlines in the input do not affect the output.
                    var meaningfulChildren = Array.from(el.childNodes).filter(function (n) { return !isIgnorableWhitespaceText_1(n); });
                    // Rule: no meaningful children => emit a self-closing tag.
                    if (meaningfulChildren.length === 0) {
                        var start = serializeElementStartTag_1(el);
                        out.push(pad + start.replace(/>$/, " />"));
                        return;
                    }
                    // Rule: exactly one text child => keep it compact on one line.
                    if (meaningfulChildren.length === 1 &&
                        meaningfulChildren[0].nodeType === Node.TEXT_NODE) {
                        var text = ((_a = meaningfulChildren[0].nodeValue) !== null && _a !== void 0 ? _a : "").trim();
                        out.push(pad + "".concat(serializeElementStartTag_1(el)).concat(text, "</").concat(el.tagName, ">"));
                        return;
                    }
                    // Rule: mixed/nested content => emit as a multi-line block.
                    out.push(pad + serializeElementStartTag_1(el));
                    meaningfulChildren.forEach(function (child) {
                        return formatNode_1(child, indentLevel + 1, out);
                    });
                    out.push(pad + "</".concat(el.tagName, ">"));
                    return;
                }
                case Node.TEXT_NODE: {
                    // Normalize whitespace inside text nodes to make output stable
                    // and readable (avoids random runs of spaces/newlines).
                    var text = ((_b = node.nodeValue) !== null && _b !== void 0 ? _b : "")
                        .replace(/\s+/g, " ")
                        .trim();
                    if (text) {
                        out.push(pad + text);
                    }
                    return;
                }
                case Node.CDATA_SECTION_NODE: {
                    // CDATA is preserved verbatim (no escaping/normalization).
                    out.push(pad + "<![CDATA[".concat((_c = node.nodeValue) !== null && _c !== void 0 ? _c : "", "]]>"));
                    return;
                }
                case Node.COMMENT_NODE: {
                    out.push(pad + "<!--".concat((_d = node.nodeValue) !== null && _d !== void 0 ? _d : "", "-->"));
                    return;
                }
                case Node.PROCESSING_INSTRUCTION_NODE: {
                    // Example: <?xml-stylesheet type="text/xsl" href="style.xsl"?>
                    var pi = node;
                    out.push(pad + "<?".concat(pi.target, " ").concat(pi.data, "?>").trim());
                    return;
                }
                default: {
                    // Fallback for node types we don't explicitly format.
                    // Serialize and include it if it contains any non-whitespace.
                    var serializer = new XMLSerializer();
                    var raw = serializer.serializeToString(node);
                    if (raw.trim()) {
                        out.push(pad + raw.trim());
                    }
                }
            }
        };
        var lines = [];
        formatNode_1(doc, 0, lines);
        return lines.join("\n");
    }
    catch (_b) {
        return xml;
    }
}
//# sourceMappingURL=prettyPrint.js.map